import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { STInvoice } from '../billing/STInvoice.js';
function isInvoiceComplete(invoice, payoutExport) {
    // Find all payout items for this invoice
    const payoutItems = payoutExport.payouts.flatMap(p => p.items).filter(i => i.invoices.find(i => i.id === invoice.id));
    // Get total sum of all payout items
    const totalPayout = payoutItems.reduce((total, item) => total + item.amount, 0);
    return totalPayout === invoice.meta.priceWithVAT;
}
export var StripePayoutItemType;
(function (StripePayoutItemType) {
    StripePayoutItemType["Invoice"] = "Invoice";
    StripePayoutItemType["StripeFees"] = "StripeFees";
    StripePayoutItemType["StripeReserved"] = "StripeReserved";
})(StripePayoutItemType || (StripePayoutItemType = {}));
export class StripePayoutItem extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = '';
        this.type = StripePayoutItemType.Invoice;
        this.amount = 0;
        this.description = '';
        this.invoices = [];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], StripePayoutItem.prototype, "name", void 0);
__decorate([
    field({ decoder: new EnumDecoder(StripePayoutItemType) })
], StripePayoutItem.prototype, "type", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], StripePayoutItem.prototype, "amount", void 0);
__decorate([
    field({ decoder: StringDecoder })
], StripePayoutItem.prototype, "description", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(STInvoice) })
], StripePayoutItem.prototype, "invoices", void 0);
export class StripePayout extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.amount = 0;
        this.statementDescriptor = '';
    }
}
__decorate([
    field({ decoder: StringDecoder })
], StripePayout.prototype, "id", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], StripePayout.prototype, "amount", void 0);
__decorate([
    field({ decoder: DateDecoder })
], StripePayout.prototype, "arrivalDate", void 0);
__decorate([
    field({ decoder: StringDecoder })
], StripePayout.prototype, "statementDescriptor", void 0);
export class StripePayoutBreakdown extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.items = [];
    }
    /**
     * Whether the payout amout matches the sum of the items
     */
    get isValid() {
        return this.payout.amount === this.items.reduce((total, item) => total + item.amount, 0);
    }
    isComplete(payoutExport) {
        for (const item of this.items) {
            if (item.type !== StripePayoutItemType.Invoice) {
                continue;
            }
            if (item.invoices.length === 0) {
                return false;
            }
            for (const invoice of item.invoices) {
                if (!isInvoiceComplete(invoice, payoutExport)) {
                    return false;
                }
            }
        }
        return true;
    }
}
__decorate([
    field({ decoder: StripePayout })
], StripePayoutBreakdown.prototype, "payout", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StripePayoutItem) })
], StripePayoutBreakdown.prototype, "items", void 0);
export class StripePayoutExport extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * All fetched payouts (we need to fetch more payouts than requested in order to complete all information on each invoice, because an invoice might have been paid out in other payouts than requested)
         */
        this.payouts = [];
    }
    get includedPayouts() {
        return this.payouts.filter(p => p.payout.arrivalDate >= this.start && p.payout.arrivalDate <= this.end);
    }
    /**
     * All payouts that only have invoices that are completely paid out in this export
     */
    get completePayouts() {
        return this.includedPayouts.filter(p => p.isComplete(this));
    }
    get totalPaidOut() {
        return this.completePayouts.reduce((total, payout) => total + payout.payout.amount, 0);
    }
    get totalStripeFees() {
        return this.completePayouts.reduce((total, payout) => total + payout.items.filter(i => i.type === StripePayoutItemType.StripeFees).reduce((total, item) => total - item.amount, 0), 0);
    }
    get totalStripeReserved() {
        return this.completePayouts.reduce((total, payout) => total + payout.items.filter(i => i.type === StripePayoutItemType.StripeReserved).reduce((total, item) => total - item.amount, 0), 0);
    }
    get totalInvoices() {
        return this.completePayouts.reduce((total, payout) => total + payout.items.filter(i => i.type === StripePayoutItemType.Invoice).reduce((total, item) => total + item.amount, 0), 0);
    }
    get totalVAT() {
        let VAT = 0;
        for (const payout of this.completePayouts) {
            for (const item of payout.items) {
                if (item.type !== StripePayoutItemType.Invoice) {
                    continue;
                }
                const invoiceVAT = item.invoices.reduce((total, invoice) => total + invoice.meta.VAT, 0);
                const invoiceTotal = item.invoices.reduce((total, invoice) => total + invoice.meta.priceWithVAT, 0);
                if (invoiceTotal === 0) {
                    continue;
                }
                // Calculate applicable VAT based on the amount of the invoice
                VAT += invoiceVAT / invoiceTotal * item.amount;
            }
        }
        return Math.round(VAT);
    }
    get net() {
        return this.totalPaidOut - this.totalVAT;
    }
    get isValid() {
        return this.totalPaidOut === this.totalInvoices - this.totalStripeFees && this.completePayouts.length === this.includedPayouts.length;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(StripePayoutBreakdown) })
], StripePayoutExport.prototype, "payouts", void 0);
__decorate([
    field({ decoder: DateDecoder })
], StripePayoutExport.prototype, "start", void 0);
__decorate([
    field({ decoder: DateDecoder })
], StripePayoutExport.prototype, "end", void 0);
//# sourceMappingURL=StripePayoutExport.js.map